import React, { useEffect, useMemo } from 'react';
import * as R from 'ramda';
import { DatePickerField } from 'Shared/form/DatePickerField';
import { TextField } from 'Shared/form/TextField';
import { useFormikContext } from 'formik';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import SelectField from 'Shared/form/SelectField';
import MedicationTableForm from './MedicationTableForm';
import { CheckboxField } from 'Shared/form/CheckboxField';
import { FaExclamationCircle } from 'react-icons/fa';
import {
    approvalOptions,
    booleanOptions,
    denialReasonOptions,
    disableActivityStatusList,
    STATUS_TYPE,
} from '../constant';
import { PhoneField } from 'App/components/PatientForm/components/PhoneField';
import CommonPatientFieldWrapper from 'App/components/PatientForm/components/CommonPatientFieldWrapper';

const tooltip = <Tooltip id="actions-tooltip">Must have an applicable protocol enabled.</Tooltip>;

const InformationForm = () => {
    const { initialValues, values, setFieldValue, validateForm } = useFormikContext();
    const { disableActivity = false, disableInputs = false } = values;
    const isStatusApproved = useMemo(() => values?.status?.id === STATUS_TYPE.approved, [values?.status]);
    const statusOptions = useMemo(() => {
        let defaultStatusOptions = Object.values(STATUS_TYPE).map((it) => ({
            id: it,
            name: it,
        }));
        if (!disableInputs) {
            defaultStatusOptions = defaultStatusOptions.filter((it) => it.id !== STATUS_TYPE.deleted);
        }
        if (!disableActivity) {
            const removeList = [STATUS_TYPE.appealed, STATUS_TYPE.appealedOverturned, STATUS_TYPE.appealedUpheld];
            defaultStatusOptions = defaultStatusOptions.filter((it) => !removeList.includes(it.id));
        }
        return defaultStatusOptions;
    }, [disableInputs, disableActivity]);

    useEffect(() => {
        const computedDisableActivity = disableActivityStatusList.includes(initialValues?.status?.id);
        const computedDisableInputs = initialValues?.status?.id === STATUS_TYPE.deleted;
        setFieldValue('disableActivity', computedDisableActivity);
        setFieldValue('disableInputs', computedDisableInputs);
    }, [initialValues?.status, setFieldValue]);

    return (
        <>
            <Row>
                <Col md={3}>
                    <SelectField
                        options={statusOptions}
                        label="Status"
                        fieldName="status"
                        isDisabled={disableActivity || disableInputs}
                        getOptionValue={R.prop('id')}
                        getOptionLabel={R.prop('name')}
                        onChange={(status) => {
                            setFieldValue('status', status);
                            setImmediate(() => validateForm());
                        }}
                    />
                </Col>
            </Row>
            {values?.status?.id !== STATUS_TYPE.notRequired && values?.status?.id !== STATUS_TYPE.denied && (
                <>
                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralPaPaNumber">
                            {(commonProps) => (
                                <Col md={3}>
                                    <TextField
                                        {...commonProps}
                                        label="PA Number"
                                        fieldName="details.paNumber"
                                        placeholder="PA Number"
                                        disabled={commonProps.disabled || disableActivity || disableInputs}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralPaPaFollowUpPhoneNumber">
                            {(commonProps) => (
                                <Col md={3}>
                                    <PhoneField
                                        {...commonProps}
                                        disabled={commonProps.disabled || disableActivity || disableInputs}
                                        fieldName="details.followUpPhone"
                                        label="PA Follow Up Phone Number"
                                        placeholder="(###) ###-####"
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralPaEffectiveStartDate">
                            {(commonProps) => (
                                <Col md={3}>
                                    <DatePickerField
                                        {...commonProps}
                                        fieldName="details.effectiveStartDate"
                                        label="Effective Start Date"
                                        disabled={commonProps.disabled || disableActivity || disableInputs}
                                        required={commonProps.required || isStatusApproved}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <CommonPatientFieldWrapper fieldName="referralPaEffectiveEndDate">
                            {(commonProps) => (
                                <Col md={3}>
                                    <DatePickerField
                                        {...commonProps}
                                        fieldName="details.effectiveEndDate"
                                        label="Effective End Date"
                                        disabled={commonProps.disabled || disableActivity || disableInputs}
                                        required={commonProps.required || isStatusApproved}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                        <Col md={12}>
                            <MedicationTableForm />
                        </Col>
                    </Row>
                    {isStatusApproved && (
                        <>
                            <Row>
                                <CommonPatientFieldWrapper fieldName="referralPaHowWasApprovalReceived">
                                    {(commonProps) => (
                                        <Col md={3}>
                                            <SelectField
                                                {...commonProps}
                                                options={approvalOptions.map((it) => ({ id: it, name: it }))}
                                                isDisabled={commonProps.disabled || disableInputs || disableActivity}
                                                label="How was approval received?"
                                                fieldName="details.methodApprovalReceived"
                                                getOptionValue={R.prop('id')}
                                                getOptionLabel={R.prop('name')}
                                            />
                                        </Col>
                                    )}
                                </CommonPatientFieldWrapper>
                            </Row>
                            <Row>
                                <CommonPatientFieldWrapper fieldName="referralPaCreatePaRenewalActivity">
                                    {(commonProps) => (
                                        <Col md={6}>
                                            <CheckboxField
                                                {...commonProps}
                                                disabled={commonProps.disabled || disableInputs || disableActivity}
                                                fieldName="details.isReAuthorizationRequired"
                                                label={
                                                    <>
                                                        Create a Prior Authorization Renewal activity (
                                                        <OverlayTrigger
                                                            trigger={['hover', 'focus']}
                                                            placement="top"
                                                            overlay={tooltip}
                                                        >
                                                            <FaExclamationCircle data-testid="icon" />
                                                        </OverlayTrigger>
                                                        )
                                                    </>
                                                }
                                            />
                                        </Col>
                                    )}
                                </CommonPatientFieldWrapper>
                            </Row>
                        </>
                    )}
                </>
            )}
            {values?.status?.id === STATUS_TYPE.denied && (
                <>
                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralPaDenialReason">
                            {(commonProps) => (
                                <Col md={3}>
                                    <SelectField
                                        {...commonProps}
                                        options={denialReasonOptions.map((it) => ({ id: it, name: it }))}
                                        isDisabled={commonProps.disabled || disableInputs || disableActivity}
                                        label="Denial Reason"
                                        fieldName="details.denialReason"
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                    </Row>
                    <Row>
                        <CommonPatientFieldWrapper fieldName="referralPaCreateAppealActivity">
                            {(commonProps) => (
                                <Col md={3}>
                                    <SelectField
                                        {...commonProps}
                                        options={booleanOptions.map((it) => ({ id: it, name: it }))}
                                        isDisabled={commonProps.disabled || disableInputs || disableActivity}
                                        label="Create an Appeal activity"
                                        fieldName="details.isPrescriberSubmittingAppeal"
                                        getOptionValue={R.prop('id')}
                                        getOptionLabel={R.prop('name')}
                                    />
                                </Col>
                            )}
                        </CommonPatientFieldWrapper>
                    </Row>
                </>
            )}
        </>
    );
};
export default InformationForm;
